/* eslint-disable typescript-sort-keys/string-enum */
/* eslint-disable no-underscore-dangle */
/*
 * Every event below is pushed to Google Tag Manager (`dataLayer`). GTM has no
 * BAA, so payloads carry no health data and no direct identifiers.
 * Allowed: event name, trial-manager ids (userId, teamId, trialId), the chat
 * question translation key, answer_type, language, device_type, timestamp,
 * partner_id, email_domain, counts and durations.
 * Never: chat answers, diseases or ICD codes, search text, emails, patient ids,
 * trial or site details, full URLs, free text.
 */
export enum GTMEvent {
  DeleteAdminAccount = 'delete_admin_account', // admin deletes their account
  DeletePatientAccount = 'delete_patient_account', // patient deletes their account
  // Share events
  DisplaySharedButtons = 'display_shared_buttons', // user sees the share buttons
  EmailShare = 'email_share', // user click on share on Email btn
  FacebookShare = 'facebook_share', // user click on share on Facebook btn
  LinkShare = 'link_share', // user copied share link
  LinkedinShare = 'linkedin_share', // user click on share on LinkedIn btn
  XShare = 'x_share', // user click on share on X btn
  // Sign up events
  SignUp = 'sign_up', // user successfully signed up
  SignUpInitial = 'sign_up_page_initial', // user first opens the sign-up page
  SignUpNextStep = 'sign_up_next_step', // user clicks next step on the sign-up page

  // Trial manager onboarding events
  TrialManagerSignUpPageView = 'trial_manager_sign_up_page_view', // trial manager first opens the sign-up page
  TrialManagerSignUpFormStarted = 'trial_manager_sign_up_form_started', // trial manager starts filling the sign-up form
  TrialManagerSignUp = 'trial_manager_sign_up', // trial manager successfully signed up
  TrialManagerSignUpFormError = 'trial_manager_sign_up_form_error', // trial manager encounters an error on server side
  TrialManagerCreateTeamPageView = 'trial_manager_create_team_page_view', // trial manager creates a team
  TrialManagerCreateTeam = 'trial_manager_create_team', // trial manager creates a team
  TrialManagerEmailVerified = 'trial_manager_email_verified', // trial manager's email is verified
  TrialManagerAccessRequestPageView = 'trial_manager_access_request_page_view', // trial manager requests access to a trial
  TrialManagerAccessRequestInstancePageView = 'trial_manager_access_request_instance_page_view', // trial manager requests access to a trial
  TrialManagerAccessRequestCompleted = 'trial_manager_access_request_completed', // trial manager successfully requested access to a trial
  TrialManagerInvitesTeamMember = 'trial_manager_invites_team_member', // trial manager sends an invite to a new team member
  TrialManagerDeletesAccount = 'trial_manager_deletes_account', // trial manager deletes their account

  // Trial manager (b2b) activity events
  TrialManagerLoginSuccess = 'trial_manager_login_success', // b2b user successfully logs in
  TrialManagerTrialsListPageView = 'trial_manager_trials_list_page_view', // b2b user views the trials list page
  TrialManagerTrialEdit = 'trial_manager_trial_edit', // b2b user edit trial details
  TrialManagerTrialDetailsPageView = 'trial_manager_trial_details_page_view', // b2b user views the trial details
  TrialManagerAddNewTrialCriterium = 'trial_manager_add_new_trial_criterium', // b2b user adds a new criterium
  TrialManagerCriteriaEdit = 'trial_manager_criteria_edit', // b2b user edits a criterium
  TrialManagerSurveyCreate = 'trial_manager_survey_create', // b2b user creates a survey
  TrialManagerSurveyLaunch = 'trial_manager_survey_launch', // b2b user launches a survey
  TrialManagerSurveyViewed = 'trial_manager_survey_viewed', // b2b user views a survey
  TrialManagerPanelCreate = 'trial_manager_panel_create', // b2b user creates a panel
  TrialManagerPanelLaunch = 'trial_manager_panel_launch', // b2b user launches a panel
  TrialManagerPanelViewed = 'trial_manager_panel_viewed', // b2b user views participants of panel

  // Insights (embedded Google Data Studio) events
  InsightsPageOpened = 'insights_page_opened', // b2b user opens the insights page
  InsightsReportLoaded = 'insights_report_loaded', // embedded report iframe finished loading
  InsightsReportError = 'insights_report_error', // insights page failed to load a report
  InsightsUpgradeClicked = 'insights_upgrade_clicked', // free-tier user clicks the upsell CTA

  // Patient events
  PatientShowPhoneNumberTrial = 'patient_show_phone_number_trial', // patient clicks on show phone number
  PatientShowEmailTrial = 'patient_show_email_trial', // patient clicks on show email
  PatientContinueQuestionnaireTrial = 'patient_continue_questionnaire_trial', // patient has to answer to questions for a trial and clicks on "answer questions"
  PatientReadyToAnswerPhysicianQuestions = 'patient_ready_to_answer_physician_questions', // patient has to answer questions for physician and clicks on ready to answer
  TrialParticipation = 'trial_participation', // patient participates in a trial

  // Track paid users activity on matching volunteers page
  RequestBoosterMailto = 'request_booster_mailto',
  TrialActiveMailto = 'trial_active_mailto',
  TrialEligibleMailto = 'trial_eligible_mailto',
  TrialVerifiedMailto = 'trial_verified_mailto',

  // Integrated chat events
  ChatBotImpression = 'chatbot_impression', // chatbot is loaded
  ChatBotVisible = 'chatbot_visible', // chatbot is visible
  ChatBotQuestionSeen = 'chatbot_question_seen', // chatbot question is seen
  ChatBotQuestionAnswered = 'chatbot_question_answered', // chatbot question is answered
  ChatBotTimeSpent = 'chatbot_time_spent', // chatbot time is spent
  ChatBotShortlistTrialNumber = 'chatbot_shortlist_trial_number', // chatbot shortlist trial number
  ChatBotEmailSubmitted = 'chatbot_email_submitted', // chatbot email is submitted
  ChatBotRegistrationCompleted = 'chatbot_registration_completed',

  // Disease search events
  DiseaseSearchPopupOpened = 'disease_search_popup_opened', // disease search popup is opened
  DiseaseSearchDiseaseSelected = 'disease_search_disease_selected', // disease is selected
  DiseaseSearchDiseaseNotFound = 'disease_search_disease_not_found', // disease is not found
  DiseaseSearchContactOpened = 'disease_search_contact_opened', // missing-disease form is opened
  DiseaseSearchContactSent = 'disease_search_contact_sent', // missing-disease message is sent
  DiseaseSearchContactEmailCopied = 'disease_search_contact_email_copied', // support email address is copied

  // Companion (milestone) events
  CompanionPopUpViewSignUp = 'companion_pop_up_view_sign_up', // user receives first pop-up for companion sign up
  CompanionEnrollmentDateSubmitted = 'companion_enrollment_date_submitted', // user fills enrollment date for trial
  CompanionUserPreferenceUpdated = 'companion_user_preference_updated', // user modified preferences linked to companion
}

type PassedEvent = {
  [key: string]: unknown;
  event: GTMEvent;
};

declare global {
  interface Window {
    dataLayer?: PassedEvent[];
    gtag?: (a: string, b: string) => void;
  }
}

export const useTrackEvents = () => {
  const pushToDataLayer = (data: PassedEvent): void => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push(data);

    // Google Ads events
    if (window.gtag) window.gtag('config', import.meta.env.VITE_GOOGLE_ADS_ID);
  };

  return {
    events: GTMEvent,
    trackEvent: (event: GTMEvent, data?: Record<string, unknown>) => {
      pushToDataLayer({
        event,
        ...data,
      });
    },
  };
};
